import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Workflow } from '../models/workflow';
import { Pageable } from '../models/pageable';
import { Job } from '../models/job';
import { WorkflowStatus } from '../models/workflowStatus';
import { WorkflowDataService } from '../data-table/workflow-data.service';
import { WorkflowDefinitionDraftRequest } from '../models/workflowDefinitionDraftRequest';
import { WorkflowHistoryEntry } from '../models/workflowHistoryEntry';
import { WorkflowDefinitionDraftResponse } from '../models/workflowDefinitionDraftResponse';
import { PublishWorkflowDefinitionRequest } from '../models/publishWorkflowDefinitionRequest';

@Injectable({ providedIn: 'root' })
export class WorkflowService extends WorkflowDataService<Workflow> {
  protected override get apiEndpoint(): string {
    return '/workflow';
  }

  simpleList(): Observable<Workflow[]> {
    return this.http.get<Workflow[]>(`${this.baseUrl + this.apiEndpoint}/simple`);
  }

  getRunningJobs(workflow: Workflow): Observable<Pageable<Job>> {
    return this.http.get<Pageable<Job>>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/running`);
  }

  getOpenJobs(workflow: Workflow): Observable<Pageable<Job>> {
    return this.http.get<Pageable<Job>>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/open`);
  }

  getResults(workflow: Workflow, size = 1000, page = 0): Observable<Pageable<Job>> {
    return this.http.get<Pageable<Job>>(
      `${this.baseUrl + this.apiEndpoint
      }/${workflow.id}/results?size=${size}&page=${page}&sort=lastUpdatedAt,desc`,
    );
  }

  getDefinition(workflow: Workflow): Observable<string> {
    return this.http.get<string>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/definition`);
  }

  getDefinitionDraft(workflow: Workflow): Observable<WorkflowDefinitionDraftResponse> {
    return this.http.get<WorkflowDefinitionDraftResponse>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/definition_draft`);
  }

  getDefinitionHistory(workflowId: string, page = 0, pageSize = 10): Observable<Pageable<WorkflowHistoryEntry>> {
    const result = this.http.get<Pageable<WorkflowHistoryEntry>>(`${this.baseUrl + this.apiEndpoint}/${workflowId}/definition_history?page=${page}&pageSize=${pageSize}`);
    return result
  }

  getDefinitionHistorySearch(workflowId: string, page = 0, pageSize = 10, query: string): Observable<Pageable<WorkflowHistoryEntry>> {
    const result = this.http.get<Pageable<WorkflowHistoryEntry>>(`${this.baseUrl + this.apiEndpoint}/${workflowId}/definition_history/search?page=${page}&pageSize=${pageSize}&query=${query}`);
    return result
  }

  getDefinitionFromHistory(workflow: Workflow, historyEntryId: string): Observable<string> {
    return this.http.get<string>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/definition_history/${historyEntryId}/definition`);
  }

  setDefinitionDraft(workflow: Workflow, definitionRequest: WorkflowDefinitionDraftRequest): Observable<WorkflowDefinitionDraftResponse> {
    return this.http.put<WorkflowDefinitionDraftResponse>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/definition_draft`, definitionRequest);
  }

  publishDefinition(workflow: Workflow, definitionRequest: PublishWorkflowDefinitionRequest): Observable<string> {
    return this.http.put<string>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/publish_definition`, definitionRequest);
  }

  activateDeactivateWorkflow(workflow: Workflow): Observable<Workflow> {
    if (workflow.status === WorkflowStatus.ACTIVE) {
      return this.disable(workflow);
    } else {
      return this.activate(workflow);
    }
  }

  activate(workflow: Workflow): Observable<Workflow> {
    return this.http.post<Workflow>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/activate`, {});
  }

  disable(workflow: Workflow): Observable<Workflow> {
    return this.http.post<Workflow>(`${this.baseUrl + this.apiEndpoint}/${workflow.id}/disable`, {});
  }
}
